/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

(() => {
  const accountComponentStyles = `
    #close-button,
    #close-button:hover {
      background: none !important;
    }

    #close-button > svg {
      width: 20px !important;
      height: 20px !important;
      color: #fff !important;
    }

    #close-button:hover > svg {
      color: #ac8e60 !important;
    }

    dialog,
    #shopify-element-wrapper dialog {
      border: 1px solid #ececec !important;
      border-radius: 8px !important;
    }

    .account,
    .account__content,
    #shopify-element-wrapper .account,
    #shopify-element-wrapper .account__content {
      background: #0d0d0d !important;
    }

    .account__header,
    #shopify-element-wrapper .account__header {
      background: #0d0d0d !important;
    }

    .account__heading,
    #shopify-element-wrapper .account__heading {
      color: #fff !important;
    }

    .account,
    .account *:not(input):not(textarea):not(select),
    #shopify-element-wrapper .account,
    #shopify-element-wrapper .account *:not(input):not(textarea):not(select) {
      color: #fff !important;
    }

    .account__header-region header > div,
    #shopify-element-wrapper .account__header-region header > div {
      padding: 0 !important;
    }

    .login-form__marketing-consent-label,
    form label,
    .account__menu-region nav a,
    #shopify-element-wrapper .login-form__marketing-consent-label,
    #shopify-element-wrapper form label,
    #shopify-element-wrapper .account__menu-region nav a {
      font-size: 13.2px !important;
    }

    .login-form__marketing-consent-label,
    #shopify-element-wrapper > div > label > span.login-form__marketing-consent-label,
    #shopify-element-wrapper .login-form__marketing-consent-label {
      color: #fff !important;
      font-size: 12px !important;
    }

    .login-form__divider-text,
    #shopify-element-wrapper > div > div > span.login-form__divider-text,
    #shopify-element-wrapper .login-form__divider-text {
      color: #fff !important;
    }

    .login-form__email-label,
    #shopify-element-wrapper > div > form > div > label {
      font-size: 13.2px !important;
    }

    .account__menu-region nav a,
    #shopify-element-wrapper > div > dialog > div > div.account__content > div.account__menu-region > div > nav > a,
    #shopify-element-wrapper .account__menu-region nav a {
      border: 1px solid #8d8d8d !important;
      color: #fff !important;
    }

    .account__menu-region nav a:hover,
    #shopify-element-wrapper > div > dialog > div > div.account__content > div.account__menu-region > div > nav > a:hover,
    #shopify-element-wrapper .account__menu-region nav a:hover {
      background: transparent !important;
      border: 1px solid #ac8e60 !important;
      color: #fff !important;
    }

    .button.outline:hover,
    #shopify-element-wrapper .button.outline:hover {
      border-color: #ac8e60 !important;
      color: #ac8e60 !important;
    }

    .account__menu-region nav a:hover,
    #shopify-element-wrapper > div > dialog > div > div.account__content > div.account__menu-region > div > nav > a:hover,
    #shopify-element-wrapper .account__menu-region nav a:hover {
      background: transparent !important;
      border: 1px solid #ac8e60 !important;
      color: #fff !important;
    }

    #login-form-email {
      border: 1px solid #0d0d0d !important;
      height: fit-content !important;
      padding: 12px 26px !important;
    }

    form button,
    #shopify-element-wrapper form button,
    #shopify-element-wrapper > div > form > div > button {
      display: flex !important;
      justify-content: center !important;
      margin-inline: auto !important;
      top: 6px !important;
    }
  `;

  const accountComponentDocumentStyles = `
    #close-button,
    #close-button:hover {
      background: none !important;
    }

    #close-button > svg {
      width: 20px !important;
      height: 20px !important;
      color: #fff !important;
    }

    #close-button:hover > svg {
      color: #ac8e60 !important;
    }

    #shopify-element-wrapper dialog {
      border: 1px solid #ececec !important;
      border-radius: 8px !important;
    }

    #shopify-element-wrapper .account,
    #shopify-element-wrapper .account__content {
      background: #0d0d0d !important;
    }

    #shopify-element-wrapper .account__header {
      background: #0d0d0d !important;
    }

    #shopify-element-wrapper .account__heading {
      color: #fff !important;
    }

    #shopify-element-wrapper .account,
    #shopify-element-wrapper .account *:not(input):not(textarea):not(select) {
      color: #fff !important;
    }

    #shopify-element-wrapper .account__header-region header > div {
      padding: 0 !important;
    }

    #shopify-element-wrapper .login-form__marketing-consent-label,
    #shopify-element-wrapper form label,
    #shopify-element-wrapper .account__menu-region nav a {
      font-size: 13.2px !important;
    }

    #shopify-element-wrapper > div > label > span.login-form__marketing-consent-label,
    #shopify-element-wrapper .login-form__marketing-consent-label {
      color: #fff !important;
      font-size: 12px !important;
    }

    #shopify-element-wrapper > div > div > span.login-form__divider-text,
    #shopify-element-wrapper .login-form__divider-text {
      color: #fff !important;
    }

    #shopify-element-wrapper .login-form__email-label,
    #shopify-element-wrapper > div > form > div > label {
      font-size: 13.2px !important;
    }

    #shopify-element-wrapper > div > dialog > div > div.account__content > div.account__menu-region > div > nav > a,
    #shopify-element-wrapper .account__menu-region nav a {
      border: 1px solid #8d8d8d !important;
      color: #fff !important;
    }

    #shopify-element-wrapper > div > dialog > div > div.account__content > div.account__menu-region > div > nav > a:hover,
    #shopify-element-wrapper .account__menu-region nav a:hover {
      background: transparent !important;
      border: 1px solid #ac8e60 !important;
      color: #fff !important;
    }

    #shopify-element-wrapper .button.outline:hover {
      border-color: #ac8e60 !important;
      color: #ac8e60 !important;
    }

    #shopify-element-wrapper > div > dialog > div > div.account__content > div.account__menu-region > div > nav > a:hover,
    #shopify-element-wrapper .account__menu-region nav a:hover {
      background: transparent !important;
      border: 1px solid #ac8e60 !important;
      color: #fff !important;
    }

    #shopify-element-wrapper #login-form-email {
      border: 1px solid #0d0d0d !important;
      height: fit-content !important;
      padding: 12px 26px !important;
    }

    #shopify-element-wrapper form button,
    #shopify-element-wrapper > div > form > div > button {
      display: flex !important;
      justify-content: center !important;
      margin-inline: auto !important;
      top: 6px !important;
    }
  `;

  const injectAccountComponentStyles = (root = document, styles = accountComponentStyles) => {
    if (!root || root.querySelector('[data-roam-account-component-styles]')) return;

    const style = document.createElement('style');
    style.setAttribute('data-roam-account-component-styles', '');
    style.textContent = styles;
    root.appendChild(style);
  };

  const forceAccountComponentStyles = (root = document) => {
    if (!root || !root.querySelectorAll) return;

    root.querySelectorAll('.account, .account__content, .account__header').forEach((element) => {
      element.style.setProperty('background', '#0d0d0d', 'important');
    });

    root.querySelectorAll('.account__heading').forEach((heading) => {
      heading.style.setProperty('color', '#fff', 'important');
    });

    root.querySelectorAll('.account *:not(input):not(textarea):not(select)').forEach((element) => {
      element.style.setProperty('color', '#fff', 'important');
    });

    root.querySelectorAll('#close-button').forEach((button) => {
      button.style.setProperty('color', '#fff', 'important');
    });

    root.querySelectorAll('#close-button > svg').forEach((icon) => {
      icon.style.setProperty('color', '#fff', 'important');
    });

    root.querySelectorAll('.login-form__divider-text').forEach((divider) => {
      divider.style.setProperty('color', '#fff', 'important');
    });

    root.querySelectorAll('.account__menu-region nav a').forEach((link) => {
      link.style.setProperty('border', '1px solid #8d8d8d', 'important');
      link.style.setProperty('color', '#fff', 'important');

      if (link.dataset.roamAccountHoverStyles === 'true') return;
      link.dataset.roamAccountHoverStyles = 'true';

      link.addEventListener('mouseenter', () => {
        link.style.setProperty('background', 'transparent', 'important');
        link.style.setProperty('border', '1px solid #ac8e60', 'important');
        link.style.setProperty('color', '#fff', 'important');
      });

      link.addEventListener('mouseleave', () => {
        link.style.setProperty('background', 'transparent', 'important');
        link.style.setProperty('border', '1px solid #8d8d8d', 'important');
        link.style.setProperty('color', '#fff', 'important');
      });
    });

    root.querySelectorAll('.login-form__marketing-consent-label').forEach((label) => {
      label.style.setProperty('color', '#fff', 'important');
      label.style.setProperty('font-size', '12px', 'important');
    });

    root.querySelectorAll('.login-form__email-label, #shopify-element-wrapper > div > form > div > label').forEach((label) => {
      label.style.setProperty('font-size', '13.2px', 'important');
    });
  };

  const scanForAccountShadowRoots = (root = document) => {
    injectAccountComponentStyles(document.head || document.documentElement, accountComponentDocumentStyles);
    forceAccountComponentStyles(root);

    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) {
        injectAccountComponentStyles(element.shadowRoot);
        forceAccountComponentStyles(element.shadowRoot);
        scanForAccountShadowRoots(element.shadowRoot);
      }
    });
  };

  const initAccountComponentStyles = () => {
    scanForAccountShadowRoots();

    if (!('MutationObserver' in window)) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          if (node.shadowRoot) {
            injectAccountComponentStyles(node.shadowRoot);
            scanForAccountShadowRoots(node.shadowRoot);
          }

          scanForAccountShadowRoots(node);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccountComponentStyles);
  } else {
    initAccountComponentStyles();
  }
})();

(() => {
  const moveCardQuickAddToFooter = (root = document) => {
    root.querySelectorAll('product-card').forEach((card) => {
      const footer = card.querySelector('.c-card__footer');
      if (!footer) return;

      card.querySelectorAll('.card__quick-add').forEach((quickAdd) => {
        if (quickAdd.parentElement === footer) return;

        footer.appendChild(quickAdd);
      });
    });
  };

  const initCardQuickAddPlacement = () => {
    moveCardQuickAddToFooter();

    if (!('MutationObserver' in window)) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          if (node.matches('product-card')) {
            moveCardQuickAddToFooter(node.parentElement || document);
          } else if (node.querySelector('product-card')) {
            moveCardQuickAddToFooter(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCardQuickAddPlacement);
  } else {
    initCardQuickAddPlacement();
  }

  document.addEventListener('shopify:section:load', (event) => {
    moveCardQuickAddToFooter(event.target);
  });
})();

/**
 * Welcome offer links. A link to #welcome-offer, such as the home page hero button
 * "/#welcome-offer", opens the sign-up pop-up (c-pop-up, in the overlay group on every page)
 * instead of navigating. Logged-in customers do not get the pop-up (show_to_guests_only), so
 * for them the link scrolls to the footer sign-up and focuses the email field. A page opened
 * with #welcome-offer in its address, from an email for example, opens the pop-up as well.
 */
(() => {
  const OFFER_HASH = '#welcome-offer';

  /**
   * Opens the pop-up, or failing that goes to the footer sign-up.
   * @param {?Element} opener - Element to return focus to when the pop-up closes.
   * @returns {boolean} Whether there was something to open.
   */
  const openWelcomeOffer = (opener) => {
    const popUp = document.querySelector('pop-up');
    if (popUp) {
      customElements.whenDefined('pop-up').then(() => {
        if (popUp.hasAttribute('open')) return;
        popUp.open(opener);

        // Modal.open focuses the pop-up element itself, which cannot take focus, so move focus
        // into the dialog for keyboard and screen reader users. Not the email field: on a phone
        // that would open the keyboard over the offer.
        const dialog = popUp.querySelector('[role="dialog"]');
        if (dialog) dialog.focus({ preventScroll: true });
      });
      return true;
    }

    const email = document.querySelector('[id^="FooterEmailSignup-"]');
    if (email) {
      email.scrollIntoView({ behavior: 'smooth', block: 'center' });
      email.focus({ preventScroll: true });
      return true;
    }

    return false;
  };

  document.addEventListener('click', (evt) => {
    const link = evt.target.closest(`a[href$="${OFFER_HASH}"]`);
    if (link && openWelcomeOffer(link)) evt.preventDefault();
  });

  if (window.location.hash === OFFER_HASH && openWelcomeOffer(null)) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
})();
